if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this._savedScrollY = null;
        this._lastFeaturedMediaId = null;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.initQuantityHandlers();
        this._readInitialFeaturedMedia();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) =>
            el.classList.add('scroll-trigger--cancel')
          )
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /* Remember which media the page loaded with, so a later SIZE change
         (same picture) doesn't re-move the gallery. */
      _readInitialFeaturedMedia() {
        try {
          const el = this.querySelector('variant-selects [data-selected-variant]');
          const v = el ? JSON.parse(el.textContent) : null;
          const id = v && v.featured_media && v.featured_media.id;
          this._lastFeaturedMediaId = id ? String(id) : null;
        } catch (e) {
          this._lastFeaturedMediaId = null;
        }
      }

      /* Holds the page on the same pixel for ~330ms after an instant variant
         update. The correction happens inside requestAnimationFrame, i.e.
         BEFORE the browser paints, so there is no visible fight or flicker.
         Any wheel / touch / key input from the shopper cancels it at once. */
      _pinScroll(y) {
        if (typeof y !== 'number') return;
        let cancelled = false;
        let frames = 0;
        const cancel = () => { cancelled = true; };
        const cleanup = () => {
          window.removeEventListener('wheel', cancel);
          window.removeEventListener('touchstart', cancel);
          window.removeEventListener('keydown', cancel);
        };
        window.addEventListener('wheel', cancel, { passive: true });
        window.addEventListener('touchstart', cancel, { passive: true });
        window.addEventListener('keydown', cancel);
        const tick = () => {
          if (cancelled || frames++ > 20) { cleanup(); return; }
          if (Math.abs((window.pageYOffset || 0) - y) > 1) {
            window.scrollTo({ top: y, left: 0, behavior: 'instant' });
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }

      /* ═══════════════════════════════════════════════════════════════
         MAIN ENTRY — called by Dawn on every variant option change
      ═══════════════════════════════════════════════════════════════ */
      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this._savedScrollY = window.pageYOffset || window.scrollY;
        this.resetProductFormState();

        /* Detect change type */
        const isColor = !!(
          event.target.closest('.lorv-color-fieldset') ||
          event.target.closest('.product-form__input--swatch') ||
          event.target.classList.contains('lorv-swatch-input') ||
          event.target.classList.contains('swatch-input__input')
        );

        /* Size pills AND the extra-option pills (heel height etc.) share
           the .lorv-size-fs class, so both are handled instantly. */
        const isSize = !!(
          event.target.closest('[data-lorv-size-group]') ||
          event.target.closest('[data-lorv-opt-group]') ||
          event.target.closest('.lorv-size-fs')
        );

        /* Color, size and extra options handled client-side — zero fetch */
        if (isColor || isSize) {
          this._handleInstant(event.target);
          return;
        }

        /* Fallback for any other option type — use Dawn's fetch */
        const productUrl = target.dataset.productUrl ||
          this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl, selectedOptionValues, shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      /* ═══════════════════════════════════════════════════════════════
         INSTANT CLIENT-SIDE UPDATE
         Handles colour, size and any extra option (heel height ...)
         without any server fetch. All data comes from the AllVariants
         JSON embedded at page load.
      ═══════════════════════════════════════════════════════════════ */
      _handleInstant(changedInput) {
        const sectionId = this.dataset.section;
        const startY = window.pageYOffset || window.scrollY || 0;

        /* Load variant data */
        const dataEl = this.querySelector('#AllVariants-' + sectionId);
        if (!dataEl) return;
        let allVariants;
        try { allVariants = JSON.parse(dataEl.textContent).variants; }
        catch(e) { return; }

        /* Read current selections — ONE entry per option group, in option
           order. null = nothing chosen yet in that group. (Reading only the
           checked radios would shift positions when an option is skipped.) */
        const vs = this.querySelector('variant-selects');
        if (!vs) return;
        const opts = [];
        vs.querySelectorAll('.product-form__input').forEach((group) => {
          const checked = group.querySelector('input[type="radio"]:checked');
          if (checked) { opts.push(checked.value); return; }
          const select = group.querySelector('select');
          opts.push(select && select.value ? select.value : null);
        });

        /* Match on whatever is chosen so far (null = not chosen yet), preferring
           an in-stock variant, so the price follows the shopper's choices even
           before every option has been picked. */
        const matches = allVariants.filter(
          (v) => opts.every((o, i) => o === null || v.options[i] === o)
        );
        const variant = matches.find((v) => v.available) || matches[0] || null;

        /* 1. Price — instant */
        this._updatePriceInstant(variant, sectionId);

        /* 2. Add to cart button — instant */
        this._updateButtonInstant(variant, sectionId);

        /* 3. Variant hidden input — needed for cart */
        this._updateVariantInputs(variant?.id);

        /* 4. URL — no page reload */
        this._updateURLInstant(variant);

        /* 5. SKU */
        this._updateSkuInstant(variant, sectionId);

        /* 6. Inventory text */
        this._updateInventoryInstant(variant, sectionId);

        /* 7. Media gallery — only move it when the variant's picture really
              changes (a colour switch). A size switch inside the same
              colour keeps whatever slide the shopper is looking at.
              keepPageScroll: the gallery scrolls inside itself only —
              the page never moves. */
        const mediaKey = variant && variant.featuredMediaId
          ? String(variant.featuredMediaId)
          : null;
        if (mediaKey && mediaKey !== this._lastFeaturedMediaId) {
          this._lastFeaturedMediaId = mediaKey;
          this.querySelector('media-gallery')?.setActiveMedia?.(
            sectionId + '-' + variant.featuredMediaId,
            false,
            { keepPageScroll: true }
          );
        }

        /* 8. Publish for other components */
        if (variant) {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, variant }
          });
        }

        /* 9. Keep the page on the same pixel while the DOM settles */
        this._pinScroll(startY);
      }

      _updatePriceInstant(variant, sectionId) {
        const el = document.getElementById('price-' + sectionId);
        if (!el) return;

        if (!variant) {
          el.innerHTML = '<div class="price price--large price--sold-out">' +
            '<div class="price__container"><div class="price__regular">' +
            '<span class="price-item price-item--regular">Unavailable</span>' +
            '</div></div></div>';
          return;
        }

        const fmt = (c) => {
          if (c === null || c === undefined) return '';
          const mf = window._lorvMoneyFormat ||
            (window._lorvMoneyFormat = (() => {
              const el2 = document.querySelector('[data-money-format]');
              return el2 ? el2.dataset.moneyFormat : '${amount}';
            })());
          const a = (c / 100).toFixed(2);
          const n = Math.round(c / 100).toString();
          return mf
            .replace(/\{\{\s*amount\s*\}\}/g, a)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, n)
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, a.replace('.', ','))
            .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
              n.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
        };

        const hasCompare = variant.compareAtPrice && variant.compareAtPrice > variant.price;
        if (hasCompare) {
          el.innerHTML = '<div class="price price--large price--on-sale">' +
            '<div class="price__container">' +
            '<div class="price__sale">' +
            '<span class="visually-hidden">Sale price</span>' +
            '<span class="price-item price-item--sale price-item--last">' +
            fmt(variant.price) + '</span></div>' +
            '<div class="price__compare">' +
            '<span class="visually-hidden">Regular price</span>' +
            '<s class="price-item price-item--regular">' +
            fmt(variant.compareAtPrice) + '</s></div>' +
            '</div></div></div>';
        } else {
          el.innerHTML = '<div class="price price--large">' +
            '<div class="price__container"><div class="price__regular">' +
            '<span class="visually-hidden">Regular price</span>' +
            '<span class="price-item price-item--regular price-item--last">' +
            fmt(variant.price) + '</span>' +
            '</div></div></div>';
        }
      }

      _updateButtonInstant(variant, sectionId) {
        const btn = document.getElementById('ProductSubmitButton-' + sectionId);
        if (!btn) return;
        const span = btn.querySelector('span');
        if (!variant) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          if (span) span.textContent = window.variantStrings?.unavailable || 'Unavailable';
        } else if (!variant.available) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          /* Always show "Sold Out" not "Unavailable" when variant exists but OOS */
          if (span) span.textContent = window.variantStrings?.soldOut || 'Sold Out';
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          if (span) span.textContent = window.variantStrings?.addToCart || 'Add to Cart';
        }
      }

      _updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      _updateURLInstant(variant) {
        if (!variant) return;
        const url = this.dataset.url + '?variant=' + variant.id;
        this.querySelector('share-button')?.updateUrl(
          (window.shopUrl || '') + url
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', url);
      }

      _updateSkuInstant(variant, sectionId) {
        const el = document.getElementById('Sku-' + sectionId);
        if (!el) return;
        if (variant?.sku) {
          el.classList.remove('hidden', 'visibility-hidden');
          const val = el.querySelector('.product__sku-number') || el;
          val.textContent = variant.sku;
        } else {
          el.classList.add('visibility-hidden');
        }
      }

      _updateInventoryInstant(variant, sectionId) {
        const el = document.getElementById('Inventory-' + sectionId);
        if (!el) return;

        /* No variant — hide */
        if (!variant) {
          el.classList.add('visibility-hidden');
          return;
        }

        /* Dot SVG helper */
        const dot = (color) =>
          '<span class="svg-wrapper" style="color:' + color + '">' +
          '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 16 16">' +
          '<circle cx="8" cy="8" r="6" fill="currentColor"/></svg></span> ';

        el.classList.remove('visibility-hidden');
        el.classList.remove('hidden');

        /* Use .available as primary signal — always present in Shopify variant JSON */
        if (variant.available === false) {
          el.innerHTML = dot('rgb(200,200,200)') + 'Out of stock';
          return;
        }

        /* If we have full inventory data, use it for low stock warning */
        if (variant.inventory_management === 'shopify' && variant.inventory_quantity !== undefined) {
          if (variant.inventory_quantity <= 0) {
            if (variant.inventory_policy === 'continue') {
              el.innerHTML = dot('rgb(62,214,96)') + 'In stock';
            } else {
              el.innerHTML = dot('rgb(200,200,200)') + 'Out of stock';
            }
          } else if (variant.inventory_quantity <= 10) {
            el.innerHTML = dot('rgb(238,148,65)') + 'Only ' + variant.inventory_quantity + ' left in stock';
          } else {
            el.innerHTML = dot('rgb(62,214,96)') + 'In stock';
          }
        } else {
          /* Fallback — available is true, just show in stock */
          el.innerHTML = dot('rgb(62,214,96)') + 'In stock';
        }
      }

      resetProductFormState() {
        this.productForm?.toggleSubmitButton(true);
        this.productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          const selector = updateFullPage
            ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);
          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const savedScrollY = this._savedScrollY ?? (window.pageYOffset || window.scrollY);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            const el = document.getElementById(targetId);
            if (el) el.focus({ preventScroll: true });
            const restore = () =>
              window.scrollTo({ top: savedScrollY, behavior: 'instant' });
            restore();
            setTimeout(restore, 0);
            setTimeout(restore, 60);
            setTimeout(restore, 200);
            this._savedScrollY = null;
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode
          ?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          if (!variant) { this.setUnavailable(); return; }
          this.updateMedia(html, variant?.featured_media?.id);
          const upd = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const dest = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && dest) {
              dest.innerHTML = source.innerHTML;
              dest.classList.toggle('hidden', shouldHide(source));
            }
          };
          upd('price');
          upd('Sku', ({ classList }) => classList.contains('hidden'));
          upd('Inventory', ({ innerText }) => innerText === '');
          upd('Volume');
          upd('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)
            ?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)
            ?.classList.remove('hidden');
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant }
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          {}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = [
          'price','Inventory','Sku','Price-Per-Item','Volume-Note','Volume','Quantity-Rules'
        ].map((id) => `#${id}-${this.dataset.section}`).join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) =>
          classList.add('hidden')
        );
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const src = this.querySelector('media-gallery ul');
        const dst = html.querySelector('media-gallery ul');
        const refresh = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const items = Array.from(src.querySelectorAll('li[data-media-id]'));
          return [
            items,
            new Set(items.map((i) => i.dataset.mediaId)),
            new Map(items.map((i, idx) => [i.dataset.mediaId, { item: i, index: idx }]))
          ];
        };
        if (src && dst) {
          let [srcItems, srcSet, srcMap] = refresh();
          const dstItems = Array.from(dst.querySelectorAll('li[data-media-id]'));
          const dstSet = new Set(dstItems.map(({ dataset }) => dataset.mediaId));
          let dirty = false;
          for (let i = dstItems.length - 1; i >= 0; i--) {
            if (!srcSet.has(dstItems[i].dataset.mediaId)) {
              src.prepend(dstItems[i]); dirty = true;
            }
          }
          for (let i = 0; i < srcItems.length; i++) {
            if (!dstSet.has(srcItems[i].dataset.mediaId)) {
              srcItems[i].remove(); dirty = true;
            }
          }
          if (dirty) [srcItems, srcSet, srcMap] = refresh();
          dstItems.forEach((dItem, dIdx) => {
            const sd = srcMap.get(dItem.dataset.mediaId);
            if (sd && sd.index !== dIdx) {
              src.insertBefore(sd.item, src.querySelector(`li:nth-of-type(${dIdx + 1})`));
              [srcItems, srcSet, srcMap] = refresh();
            }
          });
        }
        /* keepPageScroll: gallery-internal scrolling only */
        this.querySelector('media-gallery')?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`, false, { keepPageScroll: true }
        );
        const modal = this.productModal?.querySelector('.product-media-modal__content');
        const newModal = html.querySelector('product-modal .product-media-modal__content');
        if (modal && newModal) modal.innerHTML = newModal.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };
        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.quantityInput.min = min;
        if (max) { this.quantityInput.max = max; }
        else { this.quantityInput.removeAttribute('max'); }
        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        this.querySelector('.quantity__rules-cart .loading__spinner')
          ?.classList.remove('hidden');
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((r) => r.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector('.quantity__rules-cart .loading__spinner')
              ?.classList.add('hidden')
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        const updated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const upd = updated.querySelector(selector);
          if (!current || !upd) continue;
          if (selector === '.quantity__input') {
            for (const attr of ['data-cart-quantity','data-min','data-max','step']) {
              const v = upd.getAttribute(attr);
              if (v !== null) current.setAttribute(attr, v);
              else current.removeAttribute(attr);
            }
          } else {
            current.innerHTML = upd.innerHTML;
            if (selector === '.quantity__label') {
              const labelledBy = upd.getAttribute('aria-labelledby');
              if (labelledBy) {
                current.setAttribute('aria-labelledby', labelledBy);
                const cur = document.getElementById(labelledBy);
                const next = html.getElementById(labelledBy);
                if (cur && next) cur.textContent = next.textContent;
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form');
      }
      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }
      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }
      get variantSelectors() {
        return this.querySelector('variant-selects');
      }
      get relatedProducts() {
        const id = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId), 'related-products'
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${id}"]`
        );
      }
      get quickOrderList() {
        const id = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId), 'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${id}"]`);
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
